import { Head, Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button, buttonVariants } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Label } from "../../components/ui/label"
import { cn } from "../../lib/cn"

type CompanyOption = {
  id: number
  name: string
  cnpj: string
  omie_servicos: Array<{
    id: number
    label: string
  }>
}

type RecentBatch = {
  id: number
  source_filename: string
  company_name: string
  omie_servico_label?: string | null
  created_at: string | null
  status: string
  total_lines: number
  total_imported: number
  total_errors: number
}

type ImportacaoProps = {
  companies: CompanyOption[]
  recent_batches: RecentBatch[]
}

function statusLabel(status: string) {
  switch (status) {
    case "processando":
      return "Processando"
    case "importado":
      return "Importado"
    case "parcial":
      return "Importado com erros"
    case "pronto_para_exportacao":
      return "Pronto para retorno"
    default:
      return status
  }
}

export default function Importacao({ companies, recent_batches }: ImportacaoProps) {
  const form = useForm<{ company_id: string; omie_servico_id: string; file: File | null }>({
    company_id: "",
    omie_servico_id: "",
    file: null
  })
  const syncClientsForm = useForm<{ company_id: string }>({
    company_id: ""
  })

  const selectedCompany = companies.find((company) => String(company.id) === form.data.company_id)
  const availableServices = selectedCompany?.omie_servicos ?? []

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    form.post("/notas-fiscais/importacao")
  }

  function syncClients() {
    if (!form.data.company_id) return
    syncClientsForm.post("/notas-fiscais/importacao/atualizar-clientes")
  }

  return (
    <>
      <Head title="Importação em Lote" />
      <AppLayout>
        <div className="mx-auto w-full max-w-6xl space-y-6 p-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Importação em Lote NFSe</h1>
            <p className="text-slate-400">
              Envie o TXT de importação para criar as notas em massa e acompanhar até a geração do arquivo de retorno.
            </p>
          </div>

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle>Novo lote</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="grid grid-cols-1 gap-4 md:grid-cols-2" onSubmit={submit}>
                <div className="space-y-2">
                  <Label htmlFor="company_id">Empresa emissora</Label>
                  <select
                    id="company_id"
                    required
                    value={form.data.company_id}
                    onChange={(e) => {
                      form.setData("company_id", e.target.value)
                      form.setData("omie_servico_id", "")
                      syncClientsForm.setData("company_id", e.target.value)
                    }}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">Selecione a empresa</option>
                    {companies.map((company) => (
                      <option key={company.id} value={String(company.id)}>
                        {company.name} ({company.cnpj})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="omie_servico_id">Serviço Omie (fallback)</Label>
                  <select
                    id="omie_servico_id"
                    value={form.data.omie_servico_id}
                    onChange={(e) => form.setData("omie_servico_id", e.target.value)}
                    disabled={!selectedCompany}
                    className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <option value="">
                      {selectedCompany ? "Detectar automaticamente do TXT" : "Selecione a empresa primeiro"}
                    </option>
                    {availableServices.map((service) => (
                      <option key={service.id} value={String(service.id)}>
                        {service.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">
                    O serviço é identificado automaticamente pelo código do serviço no TXT. Selecione aqui apenas se quiser forçar um serviço específico ou como fallback.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file">Arquivo de importação (.txt)</Label>
                  <input
                    id="file"
                    type="file"
                    required
                    accept=".txt,text/plain"
                    onChange={(e) => form.setData("file", e.target.files?.[0] ?? null)}
                    className="block h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200 file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-2 file:py-1 file:text-slate-100"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-slate-700 bg-transparent"
                    disabled={!selectedCompany || syncClientsForm.processing}
                    onClick={syncClients}
                  >
                    {syncClientsForm.processing ? "Sincronizando..." : "Sincronizar última página Omie"}
                  </Button>
                  <Link href="/notas-fiscais" className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}>
                    Voltar
                  </Link>
                  <Button type="submit" disabled={form.processing}>
                    {form.processing ? "Importando..." : "Importar lote"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle>Lotes recentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-lg border border-slate-800">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b border-slate-800 bg-slate-950/60 text-slate-300">
                    <tr>
                      <th className="px-4 py-3 font-medium">Arquivo</th>
                      <th className="px-4 py-3 font-medium">Empresa</th>
                      <th className="px-4 py-3 font-medium">Serviço</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Importadas</th>
                      <th className="px-4 py-3 font-medium">Erros</th>
                      <th className="px-4 py-3 font-medium">Criado em</th>
                      <th className="px-4 py-3 text-right font-medium">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent_batches.length === 0 ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-slate-400" colSpan={8}>
                          Nenhum lote importado até o momento.
                        </td>
                      </tr>
                    ) : (
                      recent_batches.map((batch) => (
                        <tr key={batch.id} className="border-b border-slate-800/80 last:border-b-0">
                          <td className="px-4 py-3 text-slate-100">{batch.source_filename}</td>
                          <td className="px-4 py-3 text-slate-200">{batch.company_name}</td>
                          <td className="px-4 py-3 text-slate-300">{batch.omie_servico_label || "—"}</td>
                          <td className="px-4 py-3 text-slate-200">{statusLabel(batch.status)}</td>
                          <td className="px-4 py-3 text-slate-200">
                            {batch.total_imported}/{batch.total_lines}
                          </td>
                          <td className="px-4 py-3 text-rose-300">{batch.total_errors}</td>
                          <td className="px-4 py-3 text-slate-400">
                            {batch.created_at ? new Date(batch.created_at).toLocaleString("pt-BR") : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              href={`/notas-fiscais/importacao/${batch.id}`}
                              className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}
                            >
                              Abrir lote
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    </>
  )
}
